/**
 * AnimatedPageTitle — drop-in replacement for a vanilla <h1> in page heroes.
 *
 * Effects (all CSS-only, GPU-friendly, respects prefers-reduced-motion):
 *   • Soft pulsing glow that uses the page accent colour as its halo.
 *   • Slow gradient sweep across the letters (white → accent → secondary → white).
 *   • One-time entrance: 8px slide-up + opacity fade-in on mount.
 *   • Light-mode aware: when the user is on light theme the glow stays visible
 *     against bright backgrounds (we boost shadow opacity + add a subtle dark
 *     stroke so the white text never disappears against pale heros).
 *
 * Theme-change re-trigger: when `html.light` toggles, the gradient sweep
 * restarts so users get a clear visual confirmation that the theme switched.
 */
import { useEffect, useRef } from 'react';

export function AnimatedPageTitle({
  children,
  accent = '#00CCFF',
  secondary = '#e84393',
  className = '',
  as: Tag = 'h1',
  testId = 'page-title',
}) {
  const ref = useRef(null);

  // Re-trigger the entrance animation whenever the html.light class is toggled
  // so users get a satisfying "snap" when they switch themes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      el.classList.remove('apt-enter');
      // force reflow so the keyframes restart cleanly
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.classList.add('apt-enter');
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      data-testid={testId}
      className={`apt-title apt-enter font-['Chakra_Petch'] uppercase font-bold tracking-wide ${className}`}
      style={{
        // Custom properties consumed by the CSS keyframes below.
        '--apt-accent': accent,
        '--apt-secondary': secondary,
      }}
    >
      <span className="apt-text">{children}</span>
    </Tag>
  );
}

export default AnimatedPageTitle;
